import React, {useEffect, useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {
  PractitionerSummary,
  UpsertPractitionerTimeOffRequest,
  PartnerAdminPractitionerTimeOffSummary,
} from '@common/models'
import {DateTime} from 'luxon'
import {toInputDateTimeLocal, isValidDateTimeLocal} from '@common/utils/dates'

import {SideModal} from '~/components/general/modal/SideModal'
import {Typography} from '~/components/general/typography/Typography'
import {Container} from '~/components/general/container/Container'
import {Input} from '~/components/general/input/Input'
import FilteredSelect from '~/components/general/select/FilteredSelect'
import {Button} from '~/components/general/button/Button'
import {useGetPartnerAdminPractitioners} from '~/api/partnerAdminPractitioners'
import {
  useCreatePractitionerTimeOff,
  useUpdatePractitionerTimeOff,
} from '~/api/practitionerTimeOff'
import {useGetPartnerAdministratorPartner} from '~/api/partnerAdministrator'

type StaffTimeOffModalProps = {
  isVisible: boolean
  onClose: () => void
  timeOffSummary?: PartnerAdminPractitionerTimeOffSummary | null
}

export const StaffTimeOffModal: React.FC<StaffTimeOffModalProps> = ({
  isVisible,
  onClose,
  timeOffSummary,
}) => {
  const {t} = useTranslation<string>()

  const isEditing = !!timeOffSummary

  const [selectedPractitionerId, setSelectedPractitionerId] = useState<string>(
    timeOffSummary?.practitionerId || '',
  )
  const [startTime, setStartTime] = useState<string>(
    toInputDateTimeLocal(timeOffSummary?.startTime) || '',
  )
  const [endTime, setEndTime] = useState<string>(
    toInputDateTimeLocal(timeOffSummary?.endTime) || '',
  )

  const {data: practitioners, isLoading: isLoadingPractitioners} =
    useGetPartnerAdminPractitioners(false)

  const practitionerOptions = useMemo(
    () =>
      (practitioners || []).map((p: PractitionerSummary) => ({
        label: `${p.firstName} ${p.secondName}`.trim(),
        value: p.id,
      })),
    [practitioners],
  )

  const {mutate: createTimeOff, isPending: isCreating} =
    useCreatePractitionerTimeOff({
      practitionerId: selectedPractitionerId || '',
    })
  const {mutate: updateTimeOff, isPending: isUpdating} =
    useUpdatePractitionerTimeOff({
      practitionerId: timeOffSummary?.practitionerId || '',
      id: timeOffSummary?.id || '',
    })

  const {data: partner} = useGetPartnerAdministratorPartner()
  const partnerTimeZone = partner?.timeZone

  const startError = !isValidDateTimeLocal(startTime)
    ? t('Invalid date/time')
    : undefined
  const endError = !isValidDateTimeLocal(endTime)
    ? t('Invalid date/time')
    : undefined
  const orderingError =
    !startError && !endError && startTime && endTime && !(startTime < endTime)
      ? t('Start time must be before end time')
      : undefined

  const canSave =
    !!selectedPractitionerId &&
    !!startTime &&
    !!endTime &&
    !startError &&
    !endError &&
    !orderingError

  useEffect(() => {
    if (!isVisible) return
    if (timeOffSummary) {
      setSelectedPractitionerId(timeOffSummary.practitionerId)
      if (partnerTimeZone) {
        const start = DateTime.fromISO(timeOffSummary.startTime)
          .setZone(partnerTimeZone)
          .toFormat("yyyy-LL-dd'T'HH:mm")
        const end = DateTime.fromISO(timeOffSummary.endTime)
          .setZone(partnerTimeZone)
          .toFormat("yyyy-LL-dd'T'HH:mm")
        setStartTime(start)
        setEndTime(end)
      } else {
        setStartTime(toInputDateTimeLocal(timeOffSummary.startTime) || '')
        setEndTime(toInputDateTimeLocal(timeOffSummary.endTime) || '')
      }
    } else {
      setSelectedPractitionerId('')
      setStartTime('')
      setEndTime('')
    }
  }, [isVisible, timeOffSummary, partnerTimeZone])

  const handleSave = () => {
    if (!canSave) return

    const startDt = DateTime.fromISO(startTime)
    const endDt = DateTime.fromISO(endTime)
    const payload: UpsertPractitionerTimeOffRequest = {
      startDateTime: startDt.toFormat("yyyy-LL-dd'T'HH:mm"),
      endDateTime: endDt.toFormat("yyyy-LL-dd'T'HH:mm"),
    }

    if (isEditing && timeOffSummary) {
      updateTimeOff(payload, {
        onSuccess: () => {
          onClose()
        },
      })
    } else {
      createTimeOff(payload, {
        onSuccess: () => {
          onClose()
        },
      })
    }
  }

  return (
    <SideModal
      isVisible={isVisible}
      onClose={onClose}
      header={
        <Container>
          <Typography size="lg" weight="medium">
            {isEditing ? t('Edit time off') : t('Add time off')}
          </Typography>
        </Container>
      }>
      <Container flex direction="column" spacing={3}>
        <Container flex direction="column" spacing={1}>
          <Typography>{t('Practitioner')}</Typography>
          <FilteredSelect
            key={selectedPractitionerId || 'none'}
            width={300}
            placeholder={t('Select a practitioner')}
            options={practitionerOptions}
            initialValue={
              practitionerOptions.find(
                (o) => o.value === selectedPractitionerId,
              )?.label || ''
            }
            onChange={(value) =>
              setSelectedPractitionerId((value as string) || '')
            }
            disabled={isEditing || isLoadingPractitioners}
          />
        </Container>

        <Container flex spacing={3}>
          <Container flex direction="column" spacing={1}>
            <Typography>{t('Start time')}</Typography>
            <Input
              type="datetime-local"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              required
              errorMessage={startError || orderingError}
            />
          </Container>
          <Container flex direction="column" spacing={1}>
            <Typography>{t('End time')}</Typography>
            <Input
              type="datetime-local"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
              required
              errorMessage={endError || orderingError}
            />
          </Container>
        </Container>

        <Container flex justify="flex-end" spacing={2}>
          <Button onClick={onClose}>{t('Cancel')}</Button>
          <Button
            color="primary"
            onClick={handleSave}
            disabled={!canSave || isCreating || isUpdating}>
            {t('Save')}
          </Button>
        </Container>
      </Container>
    </SideModal>
  )
}

export default StaffTimeOffModal
